import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import LoadingOverlay from '../components/LoadingOverlay';
import TokenManager from '../utils/tokenManager';

export const AuthContext = createContext(null);

const WORKER_URL = 'https://empty-rain-2654.er-hooman.workers.dev';

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token') || localStorage.getItem('firebaseIdToken'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  // Function to update token (for registration)
  const updateToken = useCallback((newToken) => {
    setToken(newToken);
    TokenManager.setToken(newToken);
  }, []);

  // Function to validate token
  const validateToken = useCallback(async (currentToken) => {
    try {
      // Check if token is expired locally first
      if (TokenManager.isTokenExpired()) {
        console.log('Token expired locally, clearing...');
        TokenManager.clearToken();
        return false;
      }

      const response = await fetch(`${WORKER_URL}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setUserId(data.localId);
        
        // Refresh token expiration if validation successful
        TokenManager.setToken(currentToken);
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }, []);

  // Function to fetch user profile
  const fetchUserProfile = useCallback(async (currentToken) => {
    try {
      const response = await fetch(`${WORKER_URL}/api/users/me/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setUserProfile(data);
      } else if (response.status === 404) {
        setUserProfile(null);
      } else {
        setUserProfile(null);
      }
    } catch (error) {
      console.error('Fetch user profile error:', error);
      setUserProfile(null);
    }
  }, []);

  // Function to refresh token via Worker
  const refreshToken = useCallback(async (currentToken) => {
    try {
      const response = await fetch(`${WORKER_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.idToken;
      }
      return null;
    } catch (error) {
      console.error('Token refresh error:', error);
      return null;
    }
  }, []);

  // Function to login (supports both email and phone)
  const login = useCallback(async (credentials) => {
    try {
      setIsLoading(true);
      
      // Determine if it's email or phone login
      const isPhoneLogin = credentials.phone && credentials.otp;
      const endpoint = isPhoneLogin ? `${WORKER_URL}/api/auth/login/phone` : `${WORKER_URL}/api/auth/login/email`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Login failed');
      }

      const data = await response.json();
      const newToken = data.token || data.idToken;
      updateToken(newToken);
      setIsAuthenticated(true);
      // Fetch userId and profile after login
      await validateToken(newToken);
      await fetchUserProfile(newToken);
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [validateToken, fetchUserProfile]);

  // Function to logout
  const logout = useCallback(() => {
    // Clear all localStorage items that might contain user data
    localStorage.removeItem('token');
    localStorage.removeItem('firebaseIdToken');
    localStorage.removeItem('userProfile');
    localStorage.removeItem('userId');
    
    // Reset all state
    setToken(null);
    setIsAuthenticated(false);
    setUserId(null);
    setUserProfile(null);
    setIsLoading(false);
    
    console.log('AuthContext: Logout completed, all data cleared');
  }, []);

  // Function to refresh user profile
  const refreshProfile = useCallback(async () => {
    if (token) {
      await fetchUserProfile(token);
    }
  }, [token, fetchUserProfile]);

  // Validate token and fetch profile on mount and when token changes
  useEffect(() => {
    const validateAndFetch = async () => {
      if (token) {
        setIsLoading(true);
        
        // Try to refresh token if needed
        const refreshedToken = await TokenManager.refreshTokenIfNeeded();
        if (refreshedToken && refreshedToken !== token) {
          setToken(refreshedToken);
        }
        
        const isValid = await validateToken(refreshedToken || token);
        if (isValid) {
          await fetchUserProfile(refreshedToken || token);
          setIsAuthenticated(true);
        } else {
          logout();
        }
        setIsLoading(false);
      } else {
        // Clear all user data when token is null
        setUserId(null);
        setUserProfile(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        console.log('AuthContext: Token is null, cleared all user data');
      }
    };
    validateAndFetch();
  }, [token, validateToken, fetchUserProfile, logout]);

  // Setup auto-refresh on mount
  useEffect(() => {
    TokenManager.setupAutoRefresh();
  }, []);

  const value = {
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshToken,
    refreshProfile,
    updateToken,
    userId,
    userProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      <LoadingOverlay 
        isLoading={isLoading} 
        message="در حال بررسی احراز هویت..."
      />
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 